import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarDays, Check, X } from 'lucide-react';
import type { ChatSuggestedActivity, ChatSuggestedEvent, ChatSuggestedGoal } from '@/lib/api/chatCreation.service';
import { EventSummaryCard } from '@/components/events/EventSummaryCard';

interface EventReviewCardProps {
  events: ChatSuggestedEvent[];
  activitySuggestions?: Record<string, ChatSuggestedActivity[]>;
  goalSuggestions?: Record<string, ChatSuggestedGoal[]>;
  onConfirm?: (selectedEventIds: string[]) => void;
  onSkip?: () => void;
}

export const EventReviewCard: React.FC<EventReviewCardProps> = ({
  events,
  activitySuggestions,
  goalSuggestions,
  onConfirm,
  onSkip,
}) => {
  const { t } = useTranslation('celebrations');
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(
    () => new Set(events.filter(event => event.selectedByDefault !== false).map(event => event.id)),
  );
  const [expandedActivityEventIds, setExpandedActivityEventIds] = useState<Set<string>>(new Set());
  const [expandedGoalEventIds, setExpandedGoalEventIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedEventIds(new Set(events.filter(event => event.selectedByDefault !== false).map(event => event.id)));
    setExpandedActivityEventIds(new Set());
    setExpandedGoalEventIds(new Set());
  }, [events]);

  const totalSelected = selectedEventIds.size;

  if (events.length === 0) return null;

  const toggleEvent = (eventId: string) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const toggleActivities = (eventId: string) => {
    setExpandedActivityEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const toggleGoals = (eventId: string) => {
    setExpandedGoalEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  return (
    <Card className="border-primary/20 bg-primary/5 max-w-2xl w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" />
          {t('chat.eventReview.title', { defaultValue: 'Suggested events' })}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t('chat.eventReview.selectedCount', {
            selected: totalSelected,
            total: events.length,
            defaultValue: '{{selected}} of {{total}} selected',
          })}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('chat.eventReview.editableAfterCreate', {
            defaultValue: 'You can add, remove, or edit events and activities anytime after your celebration is created.',
          })}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {events.map(event => {
          const eventActivities = activitySuggestions?.[event.id] ?? [];
          const eventGoals = goalSuggestions?.[event.id] ?? [];
          const hasActivityPreview = eventActivities.length > 0;
          const hasGoalPreview = eventGoals.length > 0;
          const isActivityExpanded = expandedActivityEventIds.has(event.id);
          const isGoalExpanded = expandedGoalEventIds.has(event.id);
          const hasExpandedSection = isActivityExpanded || isGoalExpanded;

          return (
            <div key={event.id} className="space-y-0">
              <EventSummaryCard
                title={event.title}
                description={event.description}
                isSystemTemplate={event.isSystemTemplate}
                activityCount={event.activityCount}
                goalCount={event.goalCount}
                fallbackMetaText={event.eventType}
                onActivityClick={hasActivityPreview ? () => toggleActivities(event.id) : undefined}
                onGoalClick={hasGoalPreview ? () => toggleGoals(event.id) : undefined}
                activityHighlighted={isActivityExpanded}
                goalHighlighted={isGoalExpanded}
                className={`${selectedEventIds.has(event.id) ? 'border-primary/40 shadow-sm' : 'border-border/70'} ${hasExpandedSection ? 'rounded-b-none border-b-0' : ''}`}
                descriptionClassName="text-xs text-muted-foreground"
                statsClassName="mt-2 text-xs text-muted-foreground"
                leading={(
                  <input
                    type="checkbox"
                    aria-label={`Select event ${event.title}`}
                    checked={selectedEventIds.has(event.id)}
                    onChange={() => toggleEvent(event.id)}
                    className="mt-1 h-4 w-4 shrink-0 rounded border border-input"
                  />
                )}
              />

              {hasExpandedSection && (
                <div className="rounded-b-lg border border-t-0 bg-background px-4 pb-3 pt-1">
                  {isActivityExpanded && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">{t('chat.eventReview.suggestedActivities', { defaultValue: 'Suggested activities' })}</p>
                      {eventActivities.map((activity) => (
                        <div key={activity.id} className="rounded-md border border-border/60 bg-muted/40 px-2 py-1.5">
                          <p className="text-sm">{activity.title}</p>
                          {activity.category && (
                            <p className="text-[11px] text-muted-foreground">{activity.category}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {isGoalExpanded && (
                    <div className={`space-y-2 ${isActivityExpanded ? 'mt-3 pt-3 border-t' : ''}`}>
                      <p className="text-xs font-medium text-muted-foreground">{t('chat.eventReview.suggestedGoals', { defaultValue: 'Suggested goals' })}</p>
                      {eventGoals.map((goal) => (
                        <div key={goal.id} className="rounded-md border border-border/60 bg-emerald-50/60 px-2 py-1.5">
                          <p className="text-sm">{goal.title}</p>
                          <p className="text-[11px] text-muted-foreground">{t('chat.eventReview.suggestedOrganizerGoal', { defaultValue: 'Suggested organizer goal' })}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
      <CardFooter className="flex gap-2 pt-2">
        {onConfirm && (
          <Button size="sm" onClick={() => onConfirm(Array.from(selectedEventIds))} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700">
            <Check className="h-4 w-4 mr-1" />
            {t('chat.eventReview.confirmEvents', { defaultValue: 'Confirm Events' })}
          </Button>
        )}
        {onSkip && (
          <Button size="sm" variant="outline" onClick={onSkip} className="flex-1">
            <X className="h-4 w-4 mr-1" />
            {t('chat.eventReview.skipEvents', { defaultValue: 'Skip' })}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
